/**
 * MAILBOX-UNIT sale-rule dashboard — data layer (D-247, 2026-10-06).
 *
 * The rule every mailbox-unit provider page now states and the server enforces
 * (mail.com and the old GMX alias line are exempt and have their own pages):
 *   * mail ARRIVED  -> the (mailbox, site) pair is closed for everyone, forever;
 *   * NO mail (lease expiry or customer cancel) -> the mailbox is sellable again
 *     AT ONCE to OTHER customers for the same site;
 *   * the SAME customer is never given the same mailbox for the same site again;
 *   * a different site is a different right; applies from now on only (a pair
 *     burned at sale under the old D-240 rule stays closed).
 *
 * Two read-only endpoints, both under the already-enumerated
 * /admin/providers/<id>/parents ingress prefix (D-225: no new ingress leaf):
 *   GET .../parents/site-dashboard                  cards + per-site sellable stock + invariant
 *   GET .../parents/:accountId/site-history         one mailbox's sites + outcomes (lazy)
 *
 * NO customer identity is ever returned or shown (operator Q17/Q52): the history
 * shows sites, outcomes and the order link only.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from '../../api/client.js';
import type { MailboxUnitProviderId } from './mailboxUnitApi.js';
import type { MailboxTone } from './mailboxUnitApi.js';

export interface MailboxDashboardCards {
  /** Non-deleted units (mailboxes). */
  readonly total: number;
  /** Available units on allocator-eligible parents (the customer-visible stock). */
  readonly sellable: number;
  /** Units held by an order right now. */
  readonly inUse: number;
  /** Pairs closed for everyone (delivered + burned at sale under the old rule). */
  readonly closedPairs: number;
  /** Closed pairs with mail evidence. */
  readonly deliveredPairs: number;
  readonly deliveredMailboxes: number;
  /**
   * Closed pairs whose key has no order id: mail evidence unknown, so neither
   * delivered nor old rule. Optional: an api image older than this field omits it.
   */
  readonly unknownEvidencePairs?: number;
  /** Orders that produced a REAL refund (a refund ledger row, Q46). */
  readonly refundedOrders: number;
  /** Ledger serves that ended without mail. */
  readonly noMailServes: number;
  readonly openServes: number;
  /** Ledger serves without mail whose order row is missing (optional, see above). */
  readonly unclassifiedServes?: number;
  readonly ledgerServes: number;
  /** Serves recorded before the ledger (outcome unknown). */
  readonly legacyServes: number;
}

export interface MailboxSiteStockRow {
  readonly site: string;
  /** Mailboxes sellable for this site to ANY customer right now. */
  readonly sellableMailboxes: number;
  readonly closedMailboxes: number;
  readonly deliveredMailboxes: number;
  /** Closed pairs whose key has no order id (mail evidence unknown; optional). */
  readonly unknownEvidenceMailboxes?: number;
  /** Closed pairs without mail evidence (burned at sale under the old rule). */
  readonly closedByOldRuleMailboxes: number;
  /** Served without mail and still open (resellable to other customers). */
  readonly noMailMailboxes: number;
  /** Served, pair open, but the serve has no order to classify (legacy / missing order; optional). */
  readonly unclassifiedMailboxes?: number;
  readonly servedMailboxes: number;
  readonly lastClosedAt: string | null;
  readonly lastServedAt: string | null;
}

export interface MailboxInvariant {
  readonly lookBackMinutes: number;
  /** null = the probe failed (unknown, never "all good"). */
  readonly count: number | null;
  readonly orders: ReadonlyArray<{ readonly orderId: string; readonly site: string; readonly servedAt: string | null }>;
}

export interface MailboxDashboard {
  readonly providerId: string;
  readonly cards: MailboxDashboardCards;
  readonly sites: readonly MailboxSiteStockRow[];
  readonly invariants?: { readonly deliveredWithoutKey?: MailboxInvariant };
}

export type MailboxHistoryOutcome = 'in_progress' | 'delivered' | 'no_mail' | 'legacy' | 'closed';

export interface MailboxHistoryRow {
  readonly site: string;
  readonly at: string | null;
  readonly outcome: MailboxHistoryOutcome;
  /** Sellable again for this site to a customer other than the one served. */
  readonly resellable: boolean;
  /** No mail, but closed anyway: burned at sale under the old rule. */
  readonly closedByOldRule: boolean;
  readonly orderId: string | null;
  readonly orderState: string | null;
}

export interface MailboxSiteHistory {
  readonly providerId: string;
  readonly accountId: string;
  readonly address: string;
  readonly rows: readonly MailboxHistoryRow[];
}

function providerBase(providerId: string): string {
  return `/admin/providers/${encodeURIComponent(providerId)}`;
}

export async function fetchMailboxDashboard(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
): Promise<ApiResult<MailboxDashboard>> {
  return callAdminApi<MailboxDashboard>(options, { path: `${providerBase(providerId)}/parents/site-dashboard` });
}

export async function fetchMailboxSiteHistory(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  limit = 200,
): Promise<ApiResult<MailboxSiteHistory>> {
  return callAdminApi<MailboxSiteHistory>(options, {
    path: `${providerBase(providerId)}/parents/${encodeURIComponent(accountId)}/site-history?limit=${limit}`,
  });
}

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested)
// ---------------------------------------------------------------------------

/** One decimal percentage, never NaN / divide-by-zero. */
export function percentOf(part: number, total: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

/** The dashboard payload is shaped as expected (guards a stale / proxied reply). */
export function isMailboxDashboard(value: unknown): value is MailboxDashboard {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { cards?: unknown; sites?: unknown };
  return typeof v.cards === 'object' && v.cards !== null && Array.isArray(v.sites);
}

/** Operator-facing outcome label + tone of one history row. */
export function historyOutcomeLabel(row: MailboxHistoryRow): { readonly text: string; readonly tone: MailboxTone } {
  switch (row.outcome) {
    case 'delivered':
      return { text: 'Posta geldi — herkese kapalı', tone: 'success' };
    case 'in_progress':
      return { text: 'Sipariş açık', tone: 'info' };
    case 'no_mail':
      return row.closedByOldRule
        ? { text: 'Posta gelmedi — eski kuralla kapalı', tone: 'danger' }
        : { text: 'Posta gelmedi — yeniden satılabilir', tone: 'warn' };
    case 'closed':
      return row.closedByOldRule
        ? { text: 'Kapalı (eski kural / kayıt yok)', tone: 'danger' }
        : { text: 'Posta geldi — herkese kapalı', tone: 'success' };
    case 'legacy':
    default:
      return row.resellable
        ? { text: 'Eski kayıt — açık', tone: 'neutral' }
        : { text: 'Eski kayıt — kapalı', tone: 'danger' };
  }
}
